<template>
  <b-modal
    id="create-wizard"
    :title="titleText"
    header-class="create-wizard-header"
    title-class="create-label"
    footer-class="create-wizard-footer"
    no-close-on-backdrop
    @shown="fetchInitData"
    @hidden="clearForm"
  >
    <transition name="loader" v-if="isAppTypeLoading">
      <div class="loader-wrapper">
        <div class="loader"></div>
      </div>
    </transition>
    <div v-else class="wizard-container">
      <div class="content-wrapper">
        <div class="wizard-form">
          <div class="ncm-row" v-if="isInputReady">
            <label class="ncm-column-left" for="create-wizard-app-name">
              {{ nameText }}
            </label>
            <div class="ncm-column-right">
              <text-input
                id="create-wizard-app-name"
                v-model="appName"
                :focus="true"
                type="text"
                @on-text-input-change="onAppNameInputChanged"
              />
            </div>
          </div>

          <element-description
            :label="descriptionText"
            :outer-container-class="'ncm-row'"
            :label-class="'ncm-column-left'"
            :value-container-class="'ncm-column-right'"
            :elem-description="appDescription"
            @on-element-description-changed="onDeDescriptionChanged"
          ></element-description>

          <div class="ncm-row">
            <label class="ncm-column-left ncm-row-left">
              {{ typeText }}
              <a :href="helpDocLink" target="_blank">
                <svg class="info-icon">
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#info_alt"
                  ></use>
                </svg>
              </a>
            </label>
            <div class="ncm-column-right">
              <b-form-select
                v-if="!fetchAppTypeFailed"
                id="wizard-app-type"
                v-model="appType"
                :options="contentTypeText"
                @change="onTypeChange"
                size="sm"
              ></b-form-select>

              <label v-if="fetchAppTypeFailed">
                {{ $t("noAvailablePageTypes") }}
              </label>
            </div>
          </div>

          <div class="ncm-row" v-show="searchable">
            <label class="ncm-column-left">
              {{ searchBoxLabel }}
            </label>
            <div class="ncm-column-right">
              <search-box
                ref="searchInputInCreate"
                id="create-wizard-search"
                :has-icon="false"
                :box-type="'createApp'"
                :placeholder="getMessageText(messages.SEARCH)"
                :list="searchResults"
                :label-only="isLabelOnly(appType)"
                @on-search-box-item-selected="onSearchBoxItemSelected"
                @on-search-box-change="onSearchBoxChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="modal-btn modal-cancel-btn" @click="clearForm()">
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        class="modal-btn modal-save-btn"
        @click="createApp()"
        :disabled="shouldBeDisabled"
      >
        {{ getMessageText(messages.OK) }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import {
    CREATE_WIZARD_SEARCH_MAP,
    CREATE_WIZARD_SOSL_SEARCH_MAP,
    HELPER_DOCS,
    MUTATION_NAME,
    RICH_RESULT_CONTENT_TYPE,
    STATUS_CODE,
    TOAST_CONFIG
  } from "@/static/global-variables";
  import SearchBox from "@/components/SearchBox";
  import TextInput from "@/components/TextInput";
  import services from "@/services";
  import utils from "@/utils";
  import store from "@/store";
  import debounce from "lodash.debounce";
  import ElementDescription from "@/components/ElementDescription";
  import { QUERIES } from "@/static/queries";
  import territoryFeedbackLogin from "../services/territory-feedback/territoryFeedbackLogin";

  export default {
    name: "CreateAppWizard",
    components: {
      SearchBox,
      TextInput,
      ElementDescription
    },
    props: {
      asTemplate: {
        default: false,
        type: Boolean
      }
    },
    data() {
      return {
        appName: "",
        appType: "",
        searchInput: "",
        testRecord: "",
        searchBoxLabel: "",
        searchable: false,
        types: [],
        isAppTypeLoading: true,
        shouldHaveTestRecord: false,
        searchResults: [],
        appDescription: null,
        isInputReady: false
      };
    },
    computed: {
      ...mapState({
        messages: "messages",
        user: "user"
      }),
      titleText() {
        return this.asTemplate
          ? this.$t("newPageTemplate")
          : this.$t("newPage")
      },
      nameText() {
        return this.asTemplate
          ? this.$t("pageTemplateName")
          : this.$t("pageName")
      },
      typeText() {
        return this.asTemplate
          ? this.$t("pageTemplateType")
          : this.$t("pageType")
      },
      contentTypeText() {
        return this.types.map(function(element) {
          return {
            text: this.$t(utils.camelCase(element.text)),
            value: element.value
          };
        }, this);
      },
      shouldBeDisabled() {
        return (
          !this.appName ||
          !this.appType ||
          (this.shouldHaveTestRecord &&
            (!this.testRecord || this.testRecord.isEmpty))
        );
      },
      fetchAppTypeFailed() {
        return !this.types.length;
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      },
      helpDocLink() {
        return HELPER_DOCS.createContentLink;
      }
    },
    methods: {
      clearForm() {
        this.appName = "";
        this.appDescription = null;
        this.appType = "";
        this.searchInput = "";
        this.testRecord = "";
        this.searchable = false;
        this.isInputReady = false;
        this.$bvModal.hide("create-wizard");
      },
      onDeDescriptionChanged(input) {
        this.appDescription = input;
      },
      createApp() {
        const params = {
          name: this.appName,
          description: this.appDescription,
          type: this.appType.type,
          testRecord: this.testRecord.Id,
          isTemplate: this.asTemplate
        };
        services.createApp(params).then(
          resp => {
            let newApp = resp.data.data;
            newApp.dataElements = {};
            newApp.pages = [];
            newApp.isDeployable = true;

            store.commit(MUTATION_NAME.ADD_APP, newApp);

            this.$router.push({
              name: "DataPalette",
              params: {
                appId: newApp.uuid,
                appName: newApp.name,
                appDescription: newApp.description,
                type: newApp.type,
                testRecord: newApp.testRecord
              }
            });
          },
          error => {
            let message;
            if (error.response) {
              if (error.response.status === STATUS_CODE.FORBIDDEN) {
                message = this.getMessageText(this.messages.SFDC_LOGGED_OUT);

                setTimeout(() => {
                  window.location.href = `${services.studioDomain}/signin`;
                }, 5000);
              } else {
                message = this.getMessageText(this.messages.SFDC_DATA_ERROR);
              }

              this.displayErrorMessage(message);
            }
          }
        );

        this.clearForm();
      },
      performSearch(input) {
        if (
          this.appType.type ===
          CREATE_WIZARD_SEARCH_MAP.Territory_Feedback_vod.contentType
        ) {
          territoryFeedbackLogin
            .territoryFeedbackLogin(this.user.id)
            .then(resp => {
              if (!resp || resp.isManager) {
                this.handleFetchDataError();
              } else if (resp.tree && resp.tree.root) {
                this.searchResults = territoryFeedbackLogin.formatFeedbackLoginResults(
                  resp.tree.root
                );
              }
            }, this.handleFetchDataError);
        } else {
          services
            .findRecordsForObject(
              CREATE_WIZARD_SOSL_SEARCH_MAP[
                this.appType.type.replace(/ /g, "_")
              ],
              input
            )
            .then(resp => {
              if (!resp || !Array.isArray(resp.searchRecords)) {
                this.handleFetchDataError();
              } else {
                this.searchResults = resp.searchRecords;
              }
            }, this.handleFetchDataError);
        }
      },
      onSearchBoxChange: debounce(function(input) {
        this.performSearch(input);
      }, 1000),
      onSearchBoxItemSelected(selectedItem) {
        this.testRecord = selectedItem;
      },
      onAppNameInputChanged(val) {
        this.appName = val;
      },
      onTypeChange(val) {
        this.searchResults = [];
        if (
          val.type ===
          CREATE_WIZARD_SEARCH_MAP.Territory_Feedback_vod.contentType
        ) {
          this.shouldHaveTestRecord = true;
          this.$refs.searchInputInCreate.closeSelected();
          this.searchBoxLabel = utils.getVeevaMessageFromTemplate(
            this.getMessageText(this.messages.TEST_RECORD),
            this.$t("modelTerritory"),
            "Test " // need to be default English
          );
          this.searchable = true;
        } else {
          const labels = store.getters.objectNameLabels;
          const mapping = utils.getRecordTypeObject(val.type);
          this.shouldHaveTestRecord = !!mapping;
          this.$refs.searchInputInCreate.closeSelected();

          if (mapping) {
            this.searchBoxLabel = utils.getVeevaMessageFromTemplate(
              this.getMessageText(this.messages.TEST_RECORD),
              labels[mapping.object].singular,
              "Test " // need to be default English
            );
            this.searchable = true;
          } else {
            this.searchable = false;
          }
        }
      },
      fetchInitData() {
        this.fetchObjectLabel();
        this.isInputReady = true;
      },
      fetchObjectLabel() {
        if (!store.getters.objectNameLabels) {
          services.fetchObjectNames().then(
            resp => {
              let result = {
                Account: resp.Account,
                KOL: resp.Account,
                Account_Plan_vod__c: resp.Account_Plan_vod__c,
                Order_vod__c: resp.Order_vod__c,
                Inventory_Monitoring_vod__c: resp.Inventory_Monitoring_vod__c,
                EM_Speaker_vod__c: resp.EM_Speaker_vod__c,
                Call2_vod__c: resp.Call2_vod__c
              };

              store.commit(MUTATION_NAME.SET_OBJECT_NAME_LABELS, result);
              this.fetchAppTypes();
            },
            err => {
              console.error(err);
              store.commit(MUTATION_NAME.SET_OBJECT_NAME_LABELS, {});
              this.fetchAppTypes();
            }
          );
        } else {
          this.fetchAppTypes();
        }
      },
      async fetchAppTypes() {
        this.isAppTypeLoading = true;
        if (store.getters.appTypes) {
          this.types = store.getters.appTypes;
          this.isAppTypeLoading = false;
          return;
        }

        let describeObjects = null;
        try {
          describeObjects = await services.fetchObjectOLS();
        } catch (error) {
          this.types = [];
          this.handleFetchAppTypeError();
          this.isAppTypeLoading = false;
          return;
        }

        services
          .fetchAppTypes()
          .then(resp => {
            this.fetchAppTypesSuccessHandler(resp, describeObjects);
          })
          .catch(() => {
            this.types = [];
            this.handleFetchAppTypeError();
            this.isAppTypeLoading = false;
          });
      },

      fetchAppTypesSuccessHandler(resp, describeObjects) {
        if (!resp || !Array.isArray(resp.records)) {
          this.types = [];
          this.handleFetchAppTypeError();
          this.isAppTypeLoading = false;
          return;
        }
        const objectMap = new Map(
          Object.entries(store.getters.objectNameLabels)
        );
        for (let label of objectMap.keys()) {
          if (!describeObjects[label] || !describeObjects[label].createable)
            objectMap.delete(label);
        }
        const recordTypes = utils.reformatRecordTypes(resp.records, objectMap);
        this.types = utils
          .filterTypes(recordTypes, describeObjects[QUERIES.htmlReport.object])
          .map(element => {
            element.text = element.value.type;
            return element;
          })
          .sort((a, b) => {
            return a.text.localeCompare(b.text);
          });
        store.commit(MUTATION_NAME.SET_APPTYPES, this.types);
        this.isAppTypeLoading = false;
      },

      handleFetchAppTypeError() {
        this.displayErrorMessage(
          this.getMessageText(this.messages.SFDC_METADATA_ERROR)
        );
      },
      handleFetchDataError() {
        this.displayErrorMessage(
          this.getMessageText(this.messages.SFDC_DATA_ERROR)
        );
      },

      isLabelOnly(type) {
        return !RICH_RESULT_CONTENT_TYPE.includes(type.devName);
      },
      displayErrorMessage(message) {
        this.$bvToast.toast(message, TOAST_CONFIG.BOTTOM_CENTER_ERROR);
      }
    }
  };
</script>

<style scoped>
  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE);
  }

  .ncm-row-left {
    flex-direction: row;
  }

  .info-icon {
    width: 1rem;
    height: 1rem;
    margin-left: 0.25rem;
    fill: var(--TEXT_GREY_LIGHT);
  }
</style>